import React from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { CPInput } from "../CPInput";
import { TaxLineEditor } from "../shared/TaxLineEditor";
import { DeskEmpty, DeskPanel, DeskPill, DeskTable } from "../desktop/DeskTable";
import { InvoiceStatusPill } from "./invoiceDesktopParts";
import { formatCurrency } from "../../helpers/estimateTotals";
import { formatInvoiceUpdatedAt } from "../../helpers/invoices";
import { getInvoiceLineTypeMeta } from "../../helpers/invoiceLineTypes";
import { getTaxLineLabel } from "../../helpers/estimateTax";
import {
  formatPaymentTermsLabel,
  resolveInvoiceDate,
  resolveInvoiceDueDate,
} from "../../helpers/invoicePaymentTerms";
import {
  formatSettlementLabel,
  summarizeInvoiceSettlement,
} from "../../helpers/invoiceMoney";
import { getInvoiceCollectionStatus } from "../../helpers/invoiceDueStatus";
import { InvoicePaymentsSection } from "./InvoicePaymentsSection";

function Meta({ label, children }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function TotalRow({ label, value, indent = false, grand = false }) {
  return (
    <div className={`inv-row${indent ? " inv-row--indent" : ""}${grand ? " inv-row--grand" : ""}`}>
      <span className="inv-row__label">{label}</span>
      <span className="inv-row__value">{formatCurrency(value)}</span>
    </div>
  );
}

function Action({ helper, children }) {
  return (
    <div className="inv-action">
      {children}
      {helper ? <p className="inv-action__help">{helper}</p> : null}
    </div>
  );
}

function PresentationBlocks({ blocks, prefix }) {
  return blocks.map((block, index) => {
    if (block.type === "field") {
      return (
        <div key={`${prefix}-${index}`} className="inv-pi-field">
          <div className="inv-pi-field__label">{block.label}</div>
          <div className="inv-pi-field__value">{block.value}</div>
        </div>
      );
    }
    if (block.type === "text") {
      return (
        <p key={`${prefix}-${index}`} className="inv-payhelp">
          {block.text}
        </p>
      );
    }
    return null;
  });
}

const LINE_COLUMNS = [
  {
    key: "type",
    label: "Type",
    width: "16%",
    render: (line) => {
      const meta = getInvoiceLineTypeMeta(line.line_type);
      return <DeskPill tone="muted">{meta.label || line.line_type || "Line"}</DeskPill>;
    },
  },
  {
    key: "desc",
    label: "Description",
    width: "36%",
    render: (line) => (
      <span className="dt-item__text">
        <span className="dt-strong">{line.description || line.item_name || "Untitled line"}</span>
        {line.detail ? <span className="dt-sub">{line.detail}</span> : null}
      </span>
    ),
  },
  {
    key: "qty",
    label: "Qty",
    align: "right",
    width: "16%",
    render: (line) =>
      line.quantity != null ? (
        <span className="dt-num">
          {Number(line.quantity)}
          {line.unit_label ? <span className="dt-muted"> {line.unit_label}</span> : null}
        </span>
      ) : (
        <span className="dt-muted">—</span>
      ),
  },
  {
    key: "rate",
    label: "Rate",
    align: "right",
    width: "14%",
    render: (line) =>
      line.unit_amount != null ? (
        <span className="dt-num">{formatCurrency(Number(line.unit_amount))}</span>
      ) : (
        <span className="dt-muted">—</span>
      ),
  },
  {
    key: "total",
    label: "Total",
    align: "right",
    width: "18%",
    render: (line) => <span className="dt-num">{formatCurrency(Number(line.line_total || 0))}</span>,
  },
];

/**
 * Desktop Invoice Summary (management view): meta + line-item table on the
 * main canvas, financial summary and actions in the side panel.
 * State and handlers stay in InvoiceSummaryScreen; this only composes them.
 */
export function InvoiceSummaryDesktop({
  invoice,
  actions,
  saving,
  errorMessage,
  onBack,
  fields,
  derived,
  handlers,
  children,
}) {
  const {
    invoiceNumber,
    setInvoiceNumber,
    notes,
    setNotes,
    clientReference,
    setClientReference,
    taxLine1,
    setTaxLine1,
    taxLine2,
    setTaxLine2,
  } = fields;
  const {
    buckets,
    lineSubtotal,
    taxSummary,
    lineSummary,
    sourceEstimateNumber,
    showServices,
    showOther,
    paymentInstructionsPresentation,
    paymentTermsPresentation,
    customerNotesPresentation,
    invoiceFooterPresentation,
  } = derived;

  const settlement = summarizeInvoiceSettlement(invoice);
  const issued = ["sent", "paid", "archived"].includes(invoice?.status);
  const overdue =
    getInvoiceCollectionStatus(invoice) === "overdue" &&
    settlement.outstandingCents > 0;

  const countLabels = [
    ["labour", "Labour"],
    ["material", "Materials"],
    ["service", "Services"],
    ["other", "Other"],
  ].filter(([key]) => lineSummary.counts[key] > 0);

  return (
    <CrewPilotModuleShell
      title="Invoice Summary"
      subtitle={invoice.title || "Project Invoice"}
      onBack={onBack}
      desktopBackLabel="Invoice Hub"
      layout={PAGE_LAYOUT.COMMAND}
      compact
      headerActions={
        <div className="inv-head-actions">
          <InvoiceStatusPill
            status={invoice.status}
            label={
              issued
                ? formatSettlementLabel(settlement.state, overdue)
                : undefined
            }
          />
        </div>
      }
    >
      <div className="inv-detail">
        <div className="inv-main">
          <DeskPanel title="Invoice Information">
            {actions.editInvoiceNumber ? (
              <div style={{ maxWidth: 320, marginBottom: 16 }}>
                <span className="inv-field-label">Invoice Number</span>
                <CPInput label="" value={invoiceNumber} onChange={setInvoiceNumber} placeholder="INV-..." />
              </div>
            ) : null}
            <dl className="inv-meta">
              {!actions.editInvoiceNumber ? <Meta label="Invoice Number">{invoice.invoice_number}</Meta> : null}
              <Meta label="Client">{invoice.clients?.client_name || "—"}</Meta>
              <Meta label="Project">{invoice.title || "—"}</Meta>
              <Meta label="Deposit Account">
                {invoice.deposit_account?.name || invoice.deposit_account_id || "—"}
              </Meta>
              <Meta label="Payment Terms">{formatPaymentTermsLabel(invoice)}</Meta>
              <Meta label="Invoice Date">
                {formatInvoiceUpdatedAt(resolveInvoiceDate(invoice) || invoice.invoice_date)}
              </Meta>
              <Meta label="Due Date">
                {formatInvoiceUpdatedAt(resolveInvoiceDueDate(invoice) || invoice.due_date)}
              </Meta>
              {sourceEstimateNumber ? <Meta label="Source Estimate">{sourceEstimateNumber}</Meta> : null}
              <Meta label="Created">{formatInvoiceUpdatedAt(invoice.created_at || invoice.seeded_at)}</Meta>
              <Meta label="Updated">{formatInvoiceUpdatedAt(invoice.updated_at)}</Meta>
              {invoice.sent_at ? <Meta label="Sent">{formatInvoiceUpdatedAt(invoice.sent_at)}</Meta> : null}
              {invoice.paid_at ? <Meta label="Paid">{formatInvoiceUpdatedAt(invoice.paid_at)}</Meta> : null}
            </dl>
          </DeskPanel>

          <DeskPanel
            title="Line Items"
            className="dp--flush"
            actions={
              <span className="inv-lines-summary">
                <span>
                  {lineSummary.total} line{lineSummary.total === 1 ? "" : "s"}
                </span>
                {countLabels.map(([key, label]) => (
                  <span key={key}>
                    {label} · {lineSummary.counts[key]}
                  </span>
                ))}
              </span>
            }
          >
            {lineSummary.active.length === 0 ? (
              <DeskEmpty title="No active line items on this invoice." />
            ) : (
              <DeskTable
                testId="invoice-lines-table"
                caption="Invoice line items"
                className="inv-flat-table"
                columns={LINE_COLUMNS}
                rows={lineSummary.active}
                rowKey={(line) => line.line_id}
              />
            )}
          </DeskPanel>

          <div className="inv-two">
            <DeskPanel title="Client Reference">
              {actions.editClientReference ? (
                <CPInput
                  label=""
                  value={clientReference}
                  onChange={setClientReference}
                  placeholder="PO / work order (optional)"
                  disabled={saving}
                />
              ) : (
                <p className="dp-text">{clientReference?.trim() || "—"}</p>
              )}
            </DeskPanel>

            {customerNotesPresentation || actions.editNotes ? (
              <DeskPanel title="Customer Notes">
                {actions.editNotes ? (
                  <CPInput
                    label=""
                    value={notes}
                    onChange={setNotes}
                    placeholder="Customer notes (optional)"
                    disabled={saving}
                  />
                ) : (
                  <p className="dp-text">{notes?.trim() || "—"}</p>
                )}
              </DeskPanel>
            ) : null}

            {paymentInstructionsPresentation ? (
              <DeskPanel title="How to Pay">
                <PresentationBlocks blocks={paymentInstructionsPresentation.blocks} prefix="pi" />
              </DeskPanel>
            ) : null}

            {paymentTermsPresentation ? (
              <DeskPanel title="Payment Terms">
                <PresentationBlocks
                  blocks={paymentTermsPresentation.blocks.filter((b) => b.type === "field")}
                  prefix="pt"
                />
              </DeskPanel>
            ) : null}

            {invoiceFooterPresentation ? (
              <DeskPanel title="Invoice Footer">
                <p className="dp-text">{invoiceFooterPresentation.text}</p>
              </DeskPanel>
            ) : null}
          </div>

          {actions.editTax ? (
            <DeskPanel title="Tax">
              <div className="inv-two">
                <TaxLineEditor lineNumber={1} line={taxLine1} onChange={setTaxLine1} disabled={saving} />
                <TaxLineEditor lineNumber={2} line={taxLine2} onChange={setTaxLine2} disabled={saving} />
              </div>
            </DeskPanel>
          ) : null}
        </div>

        <aside className="inv-aside">
          <DeskPanel title="Financial Summary">
            <div className="inv-totals">
              <TotalRow label="Labour" value={buckets.labour} />
              <TotalRow label="Materials" value={buckets.materials} />
              {showServices ? <TotalRow label="Services" value={buckets.services} /> : null}
              {showOther ? <TotalRow label="Other" value={buckets.other} /> : null}
              <TotalRow label="Subtotal" value={lineSubtotal} />
              {Number(invoice.project_discount_percent || 0) > 0 ? (
                <TotalRow
                  label={`Project discount (${invoice.project_discount_percent}%)`}
                  value={lineSubtotal * (1 - Number(invoice.project_discount_percent) / 100)}
                  indent
                />
              ) : null}
              {Number(invoice.goodwill_adjustment || 0) !== 0 ? (
                <TotalRow label="Goodwill adjustment" value={Number(invoice.goodwill_adjustment || 0)} indent />
              ) : null}
              <TotalRow label="Taxable subtotal" value={Number(invoice.subtotal || lineSubtotal)} />
              {taxSummary.lines.map((line) => (
                <TotalRow
                  key={`${line.type}-${line.label}`}
                  label={`${getTaxLineLabel(line)} (${line.rate}%)`}
                  value={line.amount}
                  indent
                />
              ))}
              <TotalRow label="Grand Total" value={taxSummary.grandTotal} grand />
            </div>
          </DeskPanel>

          {issued ? (
            <InvoicePaymentsSection
              invoice={invoice}
              overdue={overdue}
              canReverse={Boolean(handlers.onReverse)}
              saving={saving}
              onReverse={handlers.onReverse}
              variant="desktop"
            />
          ) : null}

          <DeskPanel title="Actions">
            {errorMessage ? (
              <p role="alert" className="inv-alert">
                {errorMessage}
              </p>
            ) : null}
            <p className="dp-hint">
              This is the management view. To change line items or amounts, reopen the invoice to Draft and edit in
              Invoice Review.
            </p>
            <div className="inv-actions-list">
              {actions.editTax ? (
                <Action>
                  <button type="button" className="dt-btn dt-btn--primary" disabled={saving} onClick={handlers.onSaveDraft}>
                    {saving ? "Saving…" : "Save Draft"}
                  </button>
                </Action>
              ) : null}
              {actions.previewInvoice ? (
                <Action helper="Review the invoice layout and export a PDF when ready.">
                  <button type="button" className="dt-btn" onClick={handlers.onPreviewInvoice}>
                    Preview Invoice
                  </button>
                </Action>
              ) : null}
              {actions.exportPdf ? (
                <Action>
                  <button type="button" className="dt-btn" onClick={handlers.onExportPdf}>
                    Export PDF
                  </button>
                </Action>
              ) : null}
              {actions.markSent ? (
                <Action helper="Finalizes the invoice and records project allocations.">
                  <button type="button" className="dt-btn dt-btn--primary" disabled={saving} onClick={handlers.onSend}>
                    Send Invoice
                  </button>
                </Action>
              ) : null}
              {actions.markPaid ? (
                <Action helper="Records a receipt against the remaining balance.">
                  <button type="button" className="dt-btn dt-btn--primary" disabled={saving} onClick={handlers.onMarkPaid}>
                    Record Payment
                  </button>
                </Action>
              ) : null}
              {actions.reopenToDraft ? (
                <Action helper="Returns the invoice to Draft and unlocks project allocations for editing.">
                  <button type="button" className="dt-btn" disabled={saving} onClick={handlers.onReopen}>
                    Reopen to Draft
                  </button>
                </Action>
              ) : null}
              {actions.archive ? (
                <Action helper="Moves the invoice to Archived while keeping it available for reference.">
                  <button type="button" className="dt-btn dt-btn--danger" onClick={handlers.onArchive}>
                    Archive Invoice
                  </button>
                </Action>
              ) : null}
            </div>
          </DeskPanel>
        </aside>
      </div>
      {children}
    </CrewPilotModuleShell>
  );
}
